import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';

import { tokens } from '../../../design';

/**
 * The card shell every money-health section shares — head (icon, title,
 * subtitle, optional tag, count) + rows + optional footer.
 *
 * `tone` drives ONLY the head's icon tint and count colour, and `alert` is
 * spent on exactly two of the four sections (escalated holds, failed payouts,
 * stuck webhooks). «Окно холда закрывается» is `neutral` on purpose: the
 * automation still owns those rows, and painting them red is precisely the
 * misreading that produced this ticket.
 */
export type SectionTone = 'alert' | 'neutral';

interface SectionCardProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  /** Small badge next to the title («нужен оператор» / «наблюдение»). */
  tag?: { label: string; tone: SectionTone };
  /** Already-rendered count string — may carry a `≥` prefix. */
  count: string;
  tone?: SectionTone;
  children: ReactNode;
  footer?: ReactNode;
  'data-testid'?: string;
}

const SectionHead = ({
  icon,
  title,
  subtitle,
  tag,
  count,
  alert,
}: Pick<SectionCardProps, 'icon' | 'title' | 'subtitle' | 'tag' | 'count'> & {
  alert: boolean;
}): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2.5,
      px: 3.5,
      py: 1.5,
      borderBottom: `1px solid ${tokens.border.default}`,
      background: tokens.surface.elevated,
    }}
  >
    <Box
      aria-hidden
      sx={{
        width: 26,
        height: 26,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: alert ? tokens.state.dangerBg : tokens.surface.muted,
        color: alert ? tokens.state.danger : tokens.text.tertiary,
        '& svg': { width: 15, height: 15 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography component="h2" sx={{ fontSize: 13, fontWeight: 700, lineHeight: '17px' }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 11, lineHeight: '14px', color: tokens.text.tertiary }}>
        {subtitle}
      </Typography>
    </Box>
    {tag !== undefined ? (
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 700,
          px: 1.5,
          py: 0.25,
          borderRadius: `${tokens.radius.pill}px`,
          color: tag.tone === 'alert' ? tokens.state.danger : tokens.text.secondary,
          background: tag.tone === 'alert' ? tokens.state.dangerBg : tokens.surface.muted,
        }}
      >
        {tag.label}
      </Box>
    ) : null}
    <Typography
      data-testid="section-count"
      sx={{
        ml: 'auto',
        flexShrink: 0,
        fontFamily: tokens.font.mono,
        fontSize: 13,
        fontWeight: 700,
        color: alert ? tokens.state.danger : tokens.text.secondary,
      }}
    >
      {count}
    </Typography>
  </Box>
);

const SectionFooter = ({ children }: { children: ReactNode }): ReactElement => (
  <Box
    sx={{
      px: 3.5,
      py: 1.25,
      borderTop: `1px solid ${tokens.border.default}`,
      background: tokens.surface.elevated,
      fontSize: 11,
      lineHeight: '15px',
      color: tokens.text.tertiary,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}
  >
    {children}
  </Box>
);

export const SectionCard = ({
  icon,
  title,
  subtitle,
  tag,
  count,
  tone = 'neutral',
  children,
  footer,
  'data-testid': testId,
}: SectionCardProps): ReactElement => (
  <Box
    component="section"
    data-testid={testId}
    data-tone={tone}
    sx={{
      background: tokens.surface.base,
      border: `1px solid ${tokens.border.default}`,
      borderRadius: `${tokens.radius.card}px`,
      boxShadow: tokens.shadow.card,
      overflow: 'hidden',
    }}
  >
    <SectionHead
      icon={icon}
      title={title}
      subtitle={subtitle}
      {...(tag === undefined ? {} : { tag })}
      count={count}
      alert={tone === 'alert'}
    />
    {children}
    {footer === undefined ? null : <SectionFooter>{footer}</SectionFooter>}
  </Box>
);
